/**
 * WhoWillGuest — guest live voting UI for Who Will.
 * Only visible when a session is active; two large choices; server-authoritative.
 */
import { useEffect, useState, useCallback } from "react";
import api, { formatErr } from "../lib/api";
import { useEventSocket } from "../lib/ws";
import { useMotif } from "../contexts/MotifContext";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { T } from "../lib/i18n";

export default function WhoWillGuest({ onBack }) {
  const { eventId } = useMotif();
  const [session, setSession] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/who-will/session");
      setSession(data);
    } catch (e) { console.debug("who-will: session refresh failed", e); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEventSocket(eventId, (msg) => {
    if (msg.type === "whowill_update") refresh();
  });

  const vote = async (choice) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/who-will/vote", { choice });
      toast.success(T("Vote submitted"));
      refresh();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  const state = session?.state || "NOT_STARTED";
  const couple = session?.couple || { aimee: "AIMEE", riyaz: "RIYAZ" };
  const q = session?.current_question;
  const myVote = session?.my_vote;
  const canVote = state === "VOTING_OPEN";
  const showLive = !session?.hide_results || state === "RESULTS_REVEALED";

  return (
    <div className="space-y-4 fadein" data-testid="whowill-guest">
      <div className="flex items-center gap-2">
        {onBack && <button onClick={onBack} className="btn-ghost text-xs" data-testid="whowill-back">← {T("Back")}</button>}
        <h2 className="font-display text-xl text-motif flex-1">{T("Who Will?")}</h2>
      </div>

      {(state === "NOT_STARTED" || state === "COMPLETE" || !q) && (
        <div className="card-elev p-6 text-center text-motif-muted text-sm">
          {state === "COMPLETE" ? T("That's a wrap on Who Will — thanks for playing!") : T("The MC hasn't started the game yet. Stay tuned!")}
        </div>
      )}

      {q && state !== "NOT_STARTED" && state !== "COMPLETE" && (
        <>
          <div className="card-elev p-4 text-center">
            <div className="text-[10px] tracking-luxe text-primary-motif mb-1">{T("CURRENT QUESTION")}</div>
            <div className="font-display text-2xl text-motif" data-testid="whowill-question">{q.text}</div>
            <div className="text-[10px] tracking-luxe text-motif-muted mt-2">
              {state === "READY" && T("Voting opens soon…")}
              {state === "VOTING_OPEN" && T("VOTING IS OPEN")}
              {state === "VOTING_CLOSED" && T("Voting Closed")}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <VoteButton
              label={couple.aimee}
              selected={myVote === "aimee"}
              disabled={!canVote}
              onClick={() => vote("aimee")}
              testid="whowill-vote-aimee"
              tally={showLive ? session?.tallies?.aimee : null}
              total={session?.tallies?.total || 0}
            />
            <VoteButton
              label={couple.riyaz}
              selected={myVote === "riyaz"}
              disabled={!canVote}
              onClick={() => vote("riyaz")}
              testid="whowill-vote-riyaz"
              tally={showLive ? session?.tallies?.riyaz : null}
              total={session?.tallies?.total || 0}
            />
          </div>

          {myVote && (
            <div className="text-center text-primary-motif text-sm flex items-center justify-center gap-1" data-testid="whowill-vote-status">
              <CheckCircle2 className="w-4 h-4" /> {T("Vote submitted")}{canVote ? ` — ${T("you can still change it")}` : ""}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function VoteButton({ label, selected, disabled, onClick, testid, tally, total }) {
  const pct = total > 0 && tally != null ? Math.round((tally / total) * 100) : null;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="relative p-6 rounded-2xl border-2 text-center transition disabled:opacity-60 disabled:cursor-not-allowed hover:opacity-90"
      style={{
        borderColor: selected ? "var(--motif-primary)" : "var(--motif-border)",
        background: selected ? "var(--motif-accent)" : "var(--motif-surface)",
      }}
      data-testid={testid}
    >
      <div className="font-display text-3xl text-motif" style={{ letterSpacing: "0.08em" }}>{label}</div>
      {pct != null && (
        <div className="mt-2 text-primary-motif font-display text-xl" data-testid={`${testid}-pct`}>
          {pct}% · {tally}
        </div>
      )}
    </button>
  );
}
